import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import jsPDF from "jspdf";

const formSchema = z.object({
  projectName: z.string()
    .trim()
    .min(1, "A projekt neve kötelező")
    .max(100, "A projekt neve maximum 100 karakter lehet")
    .regex(/^[a-zA-Z0-9áéíóöőúüűÁÉÍÓÖŐÚÜŰ\s\-_.]+$/, "A projekt neve csak betűket, számokat, kötőjelet, aláhúzást és szóközt tartalmazhat"),
  pvPower: z.string()
    .trim()
    .min(1, "A PV teljesítmény megadása kötelező")
    .max(10, "Maximum 10 karakter adható meg")
    .regex(/^\d+$/, "Csak egész számok engedélyezettek")
    .refine((val) => parseInt(val) > 0, "Az érték nagyobb kell legyen mint 0"),
  batteryPower: z.string()
    .trim()
    .min(1, "Az akkumulátor teljesítmény megadása kötelező")
    .max(10, "Maximum 10 karakter adható meg")
    .regex(/^\d+$/, "Csak egész számok engedélyezettek")
    .refine((val) => parseInt(val) > 0, "Az érték nagyobb kell legyen mint 0"),
  onsiteInstallation: z.boolean().default(false),
  softwareYears: z.enum(["1", "5"]).default("1"),
});

type FormData = z.infer<typeof formSchema>;

export const EMSCalculatorForm = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showCustomQuote, setShowCustomQuote] = useState(false);
  const [calculationResults, setCalculationResults] = useState<{
    projectName: string;
    pvPower: number;
    batteryPower: number;
    onsiteInstallation: boolean;
    softwareYears: number;
    estimatedPrice: number;
  } | null>(null);
  const [pdfEmail, setPdfEmail] = useState(() => {
    return localStorage.getItem('ems_calculator_email') || '';
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      onsiteInstallation: false,
      softwareYears: "1",
    },
  });

  const onsiteInstallation = watch("onsiteInstallation");
  const softwareYears = watch("softwareYears");

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setShowCustomQuote(false);
    
    try {
      const pvPower = parseInt(data.pvPower);
      const batteryPower = parseInt(data.batteryPower);
      
      if (isNaN(pvPower) || isNaN(batteryPower)) {
        throw new Error("Invalid number format");
      }
      
      if (pvPower <= 0 || batteryPower <= 0) {
        throw new Error("Values must be positive");
      }

      const sanitizedProjectName = data.projectName
        .replace(/<[^>]*>/g, '')
        .trim();

      // Call edge function for price calculation
      const { data: priceData, error: priceError } = await supabase.functions.invoke('calculate-ems-price', {
        body: {
          pvPower,
          batteryPower,
          onsiteInstallation: data.onsiteInstallation,
          years: parseInt(data.softwareYears),
        },
      });

      if (priceError) throw priceError;

      // Check if custom quote is needed
      if (priceData.requiresCustomQuote) {
        setShowCustomQuote(true);
        setShowResults(true);
        return;
      }

      setCalculationResults({
        projectName: sanitizedProjectName,
        pvPower,
        batteryPower,
        onsiteInstallation: data.onsiteInstallation,
        softwareYears: parseInt(data.softwareYears),
        estimatedPrice: priceData.totalPrice,
      });
      setShowResults(true);
    } catch (error) {
      toast({
        title: t("ems_form_error"),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewCalculation = () => {
    setShowResults(false);
    setShowCustomQuote(false);
    setCalculationResults(null);
    setPdfEmail("");
    reset();
  };

  const generatePDF = async () => {
    if (!calculationResults) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!pdfEmail || !emailRegex.test(pdfEmail)) {
      toast({
        title: t("ems_result_email_required"),
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    if (pdfEmail.length > 255) {
      toast({
        title: "Az e-mail cím túl hosszú (maximum 255 karakter)",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    // Save calculation to database when PDF is downloaded
    try {
      const { error } = await supabase
        .from("ems_calculator_submissions")
        .insert({
          email: pdfEmail.toLowerCase().trim(),
          company_name: calculationResults.projectName,
          power_kw: calculationResults.pvPower,
          capacity_kwh: calculationResults.batteryPower,
          calculated_price: calculationResults.estimatedPrice,
          software_years: calculationResults.softwareYears,
          onsite_installation: calculationResults.onsiteInstallation,
        });

      if (error) throw error;
      
      console.log("Calculation saved to database");
    } catch (error) {
      console.error("Error saving to database:", error);
      toast({
        title: "Hiba az adatok mentése során",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let y = 25;

    // Load Roboto font from local file for Hungarian character support
    let fontLoaded = false;
    try {
      const fontResponse = await fetch('/fonts/Roboto-Regular.ttf');
      if (fontResponse.ok) {
        const arrayBuffer = await fontResponse.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        let binary = '';
        const chunkSize = 8192;
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
          binary += String.fromCharCode.apply(null, Array.from(chunk));
        }
        const fontBase64 = btoa(binary);
        
        doc.addFileToVFS("Roboto-Regular.ttf", fontBase64);
        doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");
        doc.setFont("Roboto");
        fontLoaded = true;
      }
    } catch (e) {
      console.error("Font loading failed:", e);
    }
    
    if (!fontLoaded) {
      doc.setFont("helvetica", "normal");
    }

    // Helper to calculate line height based on font size
    const getLineHeight = (fontSize: number) => fontSize * 0.5;

    // Header with logo
    try {
      const logoUrl = `${window.location.origin}/images/arbitrinity-logo.png`;
      const response = await fetch(logoUrl);
      if (response.ok) {
        const blob = await response.blob();
        const logoBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
        doc.addImage(logoBase64, "PNG", margin, 10, 29, 22);
        y = 38;
      } else {
        throw new Error("Logo fetch failed");
      }
    } catch {
      doc.setFontSize(14);
      doc.text("Arbitrinity", margin, 20);
      y = 28;
    }

    doc.setFontSize(20);
    doc.text("EMS - Indikatív Ajánlat", pageWidth / 2, y, { align: "center" });
    y += 12;

    // Greeting
    doc.setFontSize(14);
    doc.text("Kedves Leendő Partnerünk!", margin, y);
    y += 10;

    // Intro paragraphs
    doc.setFontSize(12);
    const intro1 = "Köszönjük szépen az érdeklődését a megoldásunk kapcsán. Az EMS rendszer beszerzése elengedhetetlen kellékei a stabil, optimalizált, és szabályozói előírásokat betartó erőművi működéshez.";
    const intro1Lines = doc.splitTextToSize(intro1, maxWidth);
    doc.text(intro1Lines, margin, y);
    y += intro1Lines.length * getLineHeight(12) + 4;

    const intro2 = "Minden projekt egyedi, és mi hiszünk abban, hogy a legmegfelelőbb megoldás megtalálása közös érdekünk. Emiatt a legversenyképesebb árajánlatot tudjuk adni az erőművi paraméterek alapján (PV + BESS kapacitás) és a különböző ügyféligényeket egyedileg, modulárisan tudjuk kielégíteni.";
    const intro2Lines = doc.splitTextToSize(intro2, maxWidth);
    doc.text(intro2Lines, margin, y);
    y += intro2Lines.length * getLineHeight(12) + 8;

    // Project details
    doc.setFontSize(12);
    const projectNameLines = doc.splitTextToSize(`Projekt neve: ${calculationResults.projectName}`, maxWidth);
    doc.text(projectNameLines, margin, y);
    y += projectNameLines.length * getLineHeight(12) + 2;
    
    doc.text(`PV inverter(ek) AC teljesítménye (kW): ${calculationResults.pvPower}`, margin, y);
    y += 6;
    doc.text(`Akkumulátoros inverter(ek) AC teljesítménye (kW): ${calculationResults.batteryPower}`, margin, y);
    y += 10;

    // What's included
    doc.text("Az ár tartalmazza:", margin, y);
    y += 7;
    doc.text("- Hardver", margin + 5, y);
    y += 6;
    doc.text(`- Szoftver - ${calculationResults.softwareYears} évre`, margin + 5, y);
    y += 6;
    doc.text("- Konfigurációs díj", margin + 5, y);
    y += 6;
    if (calculationResults.onsiteInstallation) {
      doc.text("- Helyszíni beüzemelés díja", margin + 5, y);
      y += 6;
    }
    y += 6;

    // Price
    doc.setFontSize(14);
    doc.text(`Indikatív árajánlatunk: ${calculationResults.estimatedPrice.toLocaleString('hu-HU')} EUR`, margin, y);
    y += 12;

    // Additional info
    doc.setFontSize(12);
    const additionalInfo = "Az egyszeri kiadáson felül, havi szinten a monitoring rendszer díja 15 euró/hónap rendszerenként, ezen felül az Arbitrinity szolgáltatás díja merül fel, egyedi megállapodás alapján.";
    const additionalLines = doc.splitTextToSize(additionalInfo, maxWidth);
    doc.text(additionalLines, margin, y);
    y += additionalLines.length * getLineHeight(12) + 4;

    // Arbitrinity info
    const arbitrinityInfo = "Arbitrinity rendszerünk növeli a napelemes rendszerek teljesítményét és megtérülését, miközben fejlett rugalmassági szolgáltatásokat biztosít a villamosenergia-piac stabilitásához. Ezt valós idejű adatelemzés, intelligens algoritmusok és rugalmas energiamenedzsment segítségével érjük el.";
    const arbitrinityLines = doc.splitTextToSize(arbitrinityInfo, maxWidth);
    doc.text(arbitrinityLines, margin, y);
    y += arbitrinityLines.length * getLineHeight(12) + 6;

    // Closing paragraph
    const closing = "Ha bármilyen kérdés felmerül, állunk rendelkezésre. Több, mint 10 éves tervezői tapasztalattal a hátunk mögött, a vezérlésnek az erőművi működésben elfoglalt helyét integratív módon közelítjük meg, ezzel segítve partnereinket a leghatékonyabb kivitelezés- és ügyfélelégedettség elérésében.";
    const closingLines = doc.splitTextToSize(closing, maxWidth);
    doc.text(closingLines, margin, y);
    y += closingLines.length * getLineHeight(12) + 8;

    // Signature
    doc.text("Üdvözlettel,", margin, y);
    y += 6;
    doc.text("A Blue Tech csapata", margin, y);

    // Footer - always at bottom
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("Blue Tech Energy Solutions Kft.", pageWidth / 2, pageHeight - 12, { align: "center" });
    doc.text("info@btes.hu  |  btes.hu", pageWidth / 2, pageHeight - 7, { align: "center" });

    doc.save(`EMS_Ajanlat_${calculationResults.projectName}.pdf`);
  };

  return (
    <section id="ems-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="container mx-auto max-w-2xl reveal-on-scroll">
        {!showResults ? (
          <>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
              {t("ems_form_title")}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 text-center">
              {t("ems_form_subtitle")}
            </p>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="projectName">{t("ems_form_project_name")}</Label>
                <Input
                  id="projectName"
                  {...register("projectName")}
                  maxLength={100}
                  placeholder="Cég neve - Projekt neve - Helyszín (pl. Teszt Kft - Új tároló - Dunaharaszti)"
                  className={errors.projectName ? "border-destructive" : ""}
                />
                {errors.projectName && (
                  <p className="text-sm text-destructive">{errors.projectName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="pvPower">{t("ems_form_pv_power")}</Label>
                <Input
                  id="pvPower"
                  type="text"
                  inputMode="numeric"
                  {...register("pvPower")}
                  maxLength={10}
                  placeholder="pl. 100"
                  className={errors.pvPower ? "border-destructive" : ""}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.pvPower && (
                  <p className="text-sm text-destructive">{errors.pvPower.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="batteryPower">{t("ems_form_battery_power")}</Label>
                <Input
                  id="batteryPower"
                  type="text"
                  inputMode="numeric"
                  {...register("batteryPower")}
                  maxLength={10}
                  placeholder="pl. 50"
                  className={errors.batteryPower ? "border-destructive" : ""}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.batteryPower && (
                  <p className="text-sm text-destructive">{errors.batteryPower.message}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label>Szoftver időtartam</Label>
                <RadioGroup
                  value={softwareYears}
                  onValueChange={(value) => setValue("softwareYears", value as "1" | "5")}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1" id="years1" />
                    <Label htmlFor="years1" className="cursor-pointer">1 év</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="5" id="years5" />
                    <Label htmlFor="years5" className="cursor-pointer">5 év</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="onsiteInstallation"
                  checked={onsiteInstallation}
                  onCheckedChange={(checked) => setValue("onsiteInstallation", checked as boolean)}
                />
                <Label
                  htmlFor="onsiteInstallation"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t("ems_form_onsite")}
                </Label>
              </div>

              <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Számítás..." : t("ems_form_submit")}
              </Button>
            </form>
          </>
        ) : showCustomQuote ? (
          <>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
              Egyedi ajánlat szükséges
            </h2>
            <div className="space-y-6 bg-gradient-to-br from-primary/5 to-secondary/5 p-8 rounded-lg text-center">
              <p className="text-lg text-muted-foreground">
                Az Ön rendszere meghaladja a 2500 kW-os online kalkulációs limitet. 
                Ilyen méretű projekteknél egyedi árajánlatot készítünk, amely figyelembe veszi 
                az Ön specifikus igényeit és körülményeit.
              </p>
              <p className="text-lg font-medium">
                Kérjük, vegye fel velünk a kapcsolatot az alábbi elérhetőségeken:
              </p>
              <p className="text-primary font-semibold">info@btes.hu</p>
              <Button 
                onClick={handleNewCalculation} 
                variant="outline" 
                size="lg"
              >
                Új kalkuláció
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">
              {t("ems_result_title")}
            </h2>
            
            {calculationResults && (
              <div className="space-y-6 bg-gradient-to-br from-primary/5 to-secondary/5 p-8 rounded-lg">
                <div className="text-center">
                  <p className="text-lg text-muted-foreground mb-2">
                    {t("ems_result_price_label")}
                  </p>
                  <p className="text-4xl font-bold text-primary">
                    {calculationResults.estimatedPrice.toLocaleString('hu-HU')} €
                  </p>
                </div>

                <div className="bg-background/50 p-4 rounded-md">
                  <p className="text-sm text-muted-foreground">
                    {t("ems_result_description")}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pdfEmail">
                    {t("ems_result_email_label")} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="pdfEmail"
                    type="email"
                    placeholder="email@example.com"
                    value={pdfEmail}
                    onChange={(e) => setPdfEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    onClick={generatePDF} 
                    size="lg" 
                    className="flex-1"
                  >
                    {t("ems_result_pdf_button")}
                  </Button>
                  <Button 
                    onClick={handleNewCalculation} 
                    variant="outline" 
                    size="lg"
                    className="flex-1"
                  >
                    {t("ems_result_new_calculation")}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};
